import { useEffect, useState } from 'react';

import { featureFlagsAPI, FeatureFlagMap } from '@/services/featureFlagsAPI';

// One fetch per session, shared across every caller. The first mount kicks
// off the request; anyone that mounts while it's in flight (or after it
// resolved) reuses the same promise/cache instead of hitting
// /api/admin/feature_flags/me again.
//
// Deliberate asymmetry: a SUCCESSFUL response is cached for the rest of the
// session (`cache` gets set), but a FAILED response is NOT cached — only the
// in-flight promise is shared, and it resolves to `{}` for whoever is
// already waiting on it. This means concurrent callers during one failed
// fetch still share that single request, but the next mount after the
// failure resolves will retry the network call rather than being stuck on
// 'off' for the rest of the session (e.g. a transient backend blip). See
// the "retries after a failed fetch" tests below for the behavior this
// guarantees.
let cache: FeatureFlagMap | null = null;
let inflight: Promise<FeatureFlagMap> | null = null;

// Visible for tests (module-singleton cache would otherwise leak between
// `it` blocks in the same file). Mirrors __resetVexoSessionForTests.
export function __resetFeatureFlagCacheForTests(): void {
  cache = null;
  inflight = null;
}

const load = (): Promise<FeatureFlagMap> => {
  if (cache) return Promise.resolve(cache);
  if (!inflight) {
    inflight = featureFlagsAPI
      .me()
      .then((map) => {
        // Only a fulfilled response warms the session cache — see the
        // asymmetry note above `cache`'s declaration.
        cache = map;
        return map;
      })
      .catch(() => ({}))
      .finally(() => {
        inflight = null;
      });
  }
  return inflight;
};

export const useFeatureFlag = (
  flagKey: string,
): { variant: string; loading: boolean } => {
  const [variant, setVariant] = useState<string>(cache?.[flagKey] ?? 'off');
  const [loading, setLoading] = useState<boolean>(cache === null);

  useEffect(() => {
    let active = true;
    load().then((map) => {
      if (!active) return;
      setVariant(map[flagKey] ?? 'off');
      setLoading(false);
    });
    return () => {
      active = false;
    };
  }, [flagKey]);

  return { variant, loading };
};

export default useFeatureFlag;
